import type { ComponentPropsWithoutRef, ReactNode, ReactElement } from 'react';
import { Link as RouterLink } from 'react-router';
import { ModalTrigger } from './ModalTrigger';
import { SlideoutTrigger } from './SlideoutTrigger';
import { Modal } from './Modal';
import { Slideout } from './Slideout';

interface BaseLinkProps extends Omit<ComponentPropsWithoutRef<'a'>, 'href'> {
  children: ReactNode;
  onClick?: (() => void) | undefined;
}

interface InternalLinkProps extends BaseLinkProps {
  href: string;
  slideout?: never;
  modal?: never;
  external?: never;
}

interface SlideoutLinkProps extends BaseLinkProps {
  slideout: {
    title: string;
    subtitle?: string;
    content: ReactNode | ((props: { close: () => void; isEntering: boolean; isExiting: boolean }) => ReactNode);
    buttons?: ReactElement[];
    hideCloseButton?: boolean;
  };
  href?: never;
  modal?: never;
  external?: never;
}

interface ModalLinkProps extends BaseLinkProps {
  modal: {
    title: string;
    subtitle?: string;
    content: ReactNode | ((props: { close: () => void; isEntering: boolean; isExiting: boolean }) => ReactNode);
    buttons?: ReactElement[];
    hideCloseButton?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  };
  href?: never;
  slideout?: never;
  external?: never;
}

interface ExternalLinkProps extends BaseLinkProps {
  external: string;
  href?: never;
  slideout?: never;
  modal?: never;
}

type LinkProps = InternalLinkProps | SlideoutLinkProps | ModalLinkProps | ExternalLinkProps;

export const Link = ({ children, ...props }: LinkProps) => {
  if ('external' in props && props.external) {
    const { external, ...restProps } = props;
    return (
      <a 
        href={external} 
        target="_blank" 
        rel="noopener noreferrer" 
        {...restProps}
      >
        {children}
      </a>
    );
  }

  if ('modal' in props && props.modal) {
    const { modal, className, onClick } = props;
    const modalContent = (
      <Modal
        title={modal.title}
        subtitle={modal.subtitle}
        buttons={modal.buttons}
        hideCloseButton={modal.hideCloseButton}
        size={modal.size}
      >
        {modal.content}
      </Modal>
    );
    return <ModalTrigger content={modalContent} className={className} onClick={onClick}>{children}</ModalTrigger>;
  }

  if ('slideout' in props && props.slideout) {
    const { slideout, className, onClick } = props;
    const slideoutContent = (
      <Slideout
        title={slideout.title}
        subtitle={slideout.subtitle}
        buttons={slideout.buttons}
        hideCloseButton={slideout.hideCloseButton}
      >
        {slideout.content}
      </Slideout>
    );
    return <SlideoutTrigger content={slideoutContent} className={className} onClick={onClick}>{children}</SlideoutTrigger>;
  }

  if ('href' in props && props.href) {
    const { href, ...restProps } = props;
    return <RouterLink to={href} {...restProps}>{children}</RouterLink>;
  }

  // Fallback - just render a span since we don't have a valid link type
  return <span {...props}>{children}</span>;
};